import { useCallback, useState } from 'react'
import { FaWaveSquare, FaRulerHorizontal, FaGripLines } from 'react-icons/fa'
import ContextualMenu from 'components/graph/controls/contextualMenu'
import KindControl from 'components/graph/controls/kindControl'
import KindColorControl from 'components/graph/controls/kindColorControl'
import KindVisibilityControl from 'components/graph/controls/kindVisibilityControl'
import KindSpringControl from 'components/graph/controls/kindSpringControl'
import KindDirectionControl from 'components/graph/controls/kindDirectionControl'
import KindDirectionStrictnessControl from 'components/graph/controls/kindDirectionStrictnessControl'
import ValuePickerControl from 'components/graph/controls/valuePickerControl'
import KindSelectModal from 'components/graph/KindSelectModal'
import type { KindEntity, KindMap } from 'types/domain'

const STIFFNESS_OPTIONS = [
  { value: 100, label: 'Soft', badge: 'Soft' },
  { value: 500, label: 'Medium', badge: 'Med' },
  { value: 800, label: 'Stiff', badge: 'Stiff' },
]
const DEFAULT_STIFFNESS = 500

const LENGTH_OPTIONS = [
  { value: 1, label: '1', badge: '1' },
  { value: 20, label: '2', badge: '2' },
  { value: 50, label: '3', badge: '3' },
  { value: 70, label: '4', badge: '4' },
  { value: 90, label: '5', badge: '5' },
]
const DEFAULT_LENGTH = 20

const THICKNESS_OPTIONS = [
  { value: 1, label: 'Thin', badge: 'Thin' },
  { value: 2, label: 'Medium', badge: 'Med' },
  { value: 4, label: 'Thick', badge: 'Thick' },
]
const DEFAULT_THICKNESS = 1

type KindMenuProps = {
  open: boolean
  onCollapse: () => void
  onWidthChange?: ((width: number) => void) | undefined
  kind: KindEntity | null
  kinds?: KindMap | undefined
  objectType: 'node' | 'edge'
  title: string
  onChangeKindColor: (color: string) => void
  onToggleEdgeVisible: () => void
  onToggleEdgeSpring: () => void
  onCycleEdgeDirection: () => void
  onToggleEdgeDirectionSoft: () => void
  onChangeEdgeStiffness: (value: number) => void
  onChangeEdgeLength: (value: number) => void
  onChangeEdgeThickness: (value: number) => void
  onSelectKind: (kindId: string) => void
  editOneKind?: ((payload: { id: string; label: string; styles: Record<string, unknown> }) => void) | undefined
  createOneKind?: ((payload: { id: string; label: string; styles: { color: string } }) => void) | undefined
}

const KindMenu = ({
  open,
  onCollapse,
  onWidthChange,
  kind,
  kinds,
  objectType,
  title,
  onChangeKindColor,
  onToggleEdgeVisible,
  onToggleEdgeSpring,
  onCycleEdgeDirection,
  onToggleEdgeDirectionSoft,
  onChangeEdgeStiffness,
  onChangeEdgeLength,
  onChangeEdgeThickness,
  onSelectKind,
  editOneKind,
  createOneKind,
}: KindMenuProps) => {
  const [kindSelectOpen, setKindSelectOpen] = useState(false)

  const openKindSelect = useCallback(() => setKindSelectOpen(true), [])
  const closeKindSelect = useCallback(() => setKindSelectOpen(false), [])

  const handleSelectKind = useCallback((kindId: string) => {
    onSelectKind(kindId)
    closeKindSelect()
  }, [onSelectKind, closeKindSelect])

  return (
    <ContextualMenu open={open} onCollapse={onCollapse} onWidthChange={onWidthChange}>
      <KindControl kind={kind} onClick={openKindSelect} />
      {kind ? <KindColorControl color={kind.data.styles.color} onChange={onChangeKindColor} /> : null}
      {objectType === 'edge' && kind ? (
        <KindVisibilityControl visible={kind.data.styles.edgeVisible !== false} onClick={onToggleEdgeVisible} />
      ) : null}
      {objectType === 'edge' && kind ? (
        <KindSpringControl pullsTogether={kind.data.styles.edgeSpring !== false} onClick={onToggleEdgeSpring} />
      ) : null}
      {objectType === 'edge' && kind ? (
        <KindDirectionControl direction={kind.data.styles.edgeDirection ?? 'free'} onClick={onCycleEdgeDirection} />
      ) : null}
      {objectType === 'edge' && kind && (kind.data.styles.edgeDirection ?? 'free') !== 'free' ? (
        <KindDirectionStrictnessControl soft={Boolean(kind.data.styles.edgeDirectionSoft)} onClick={onToggleEdgeDirectionSoft} />
      ) : null}
      {objectType === 'edge' && kind ? (
        <ValuePickerControl
          icon={FaWaveSquare}
          label="Stiffness"
          options={STIFFNESS_OPTIONS}
          value={kind.data.styles.edgeStiffness ?? DEFAULT_STIFFNESS}
          onSelect={onChangeEdgeStiffness}
        />
      ) : null}
      {objectType === 'edge' && kind ? (
        <ValuePickerControl
          icon={FaRulerHorizontal}
          label="Length"
          options={LENGTH_OPTIONS}
          value={kind.data.styles.edgeSpringLength ?? DEFAULT_LENGTH}
          onSelect={onChangeEdgeLength}
        />
      ) : null}
      {objectType === 'edge' && kind ? (
        <ValuePickerControl
          icon={FaGripLines}
          label="Thickness"
          options={THICKNESS_OPTIONS}
          value={kind.data.styles.edgeThickness ?? DEFAULT_THICKNESS}
          onSelect={onChangeEdgeThickness}
        />
      ) : null}
      <KindSelectModal
        open={kindSelectOpen}
        onClose={closeKindSelect}
        kinds={kinds}
        kindId={kind?.id ?? ''}
        title={title}
        onSelectKind={handleSelectKind}
        editOneKind={editOneKind}
        createOneKind={createOneKind}
        objectType={objectType}
      />
    </ContextualMenu>
  )
}

export default KindMenu
